<template>
    <div class="layout-px-spacing">
        <teleport to="#breadcrumb">
            <ul class="navbar-nav flex-row">
                <li>
                    <div class="page-header">
                        <nav class="breadcrumb-one" aria-label="breadcrumb">
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item active" aria-current="page">
                                    <span>{{ isEditMode ? 'Edit' : 'Create' }} Violation</span>
                                </li>
                            </ol>
                        </nav>
                    </div>
                </li>
            </ul>
        </teleport>

        <div class="row layout-top-spacing">
            <div class="col-xl-6 col-lg-6 col-md-12 col-12 layout-spacing">
                <div class="panel br-4">
                    <div class="panel-body">
                        <template v-if="hasEditPermission || !isEditMode">
                            <router-link to="/violation/view">
                                <button type="button" class="btn btn-danger mb-2 me-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round" class="feather feather-arrow-left">
                                        <line x1="19" y1="12" x2="5" y2="12"></line>
                                        <polyline points="12 19 5 12 12 5"></polyline>
                                    </svg>
                                    Back to Violations
                                </button>
                            </router-link>
                            <p class="mb-0 mt-3">{{ isEditMode ? 'Edit' : 'Create' }} your Violation Details here...</p>

                            <!-- Form -->
                            <form @submit.prevent="handleSubmit">
                                <div class="column">
                                    <div class="col-md-12">
                                        <div class="mb-4">
                                            <label class="col-form-label" for="title">Violation Name</label>
                                            <input v-model="formData.title" type="text" id="title" class="form-control"
                                                placeholder="Required" required :disabled="loading" />
                                        </div>
                                        <div class="mb-4">
                                            <label class="col-form-label" for="description">Description</label>
                                            <textarea v-model="formData.description" id="description"
                                                class="form-control" placeholder="Required" required rows="4"
                                                :disabled="loading"></textarea>
                                        </div>
                                    </div>
                                </div>

                                <div class="text-center mt-3">
                                    <div v-if="loading">
                                        <button type="button" class="btn btn-success btn-lg mb-3 me-3">
                                            <span
                                                class="spinner-grow text-white me-2 align-self-center loader-sm">Loading...</span>
                                            Processing
                                        </button>
                                    </div>
                                    <div v-else>
                                        <button type="submit" class="btn btn-success btn-lg mb-3 me-3">
                                            {{ isEditMode ? 'Update' : 'Create' }}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </template>
                        <div v-else class="alert alert-danger text-center">
                            <strong>Access Denied!</strong> You do not have permission to edit violations.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.layout-px-spacing {
    min-height: calc(100vh - 170px) !important;
}
</style>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import { BASE_URL } from '/src/Config/config';
import { useMeta } from "/src/composables/use-meta";
import Swal from 'sweetalert2';
import { useStore } from 'vuex';

useMeta({ title: "Violation Details" });

const route = useRoute();
const router = useRouter();
const store = useStore();

// Form data
const formData = ref({
    id: null,
    title: '',
    description: ''
});

// State
const loading = ref(false);
const isEditMode = ref(false);

// Permission checks
const hasEditPermission = computed(() => {
    return isEditMode.value
        ? store.getters.hasPermission('edit_violation')
        : store.getters.hasPermission('create_violation');
});

// Alert functions
const showSuccessAlert = (message) => {
    Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: message,
        padding: '2em',
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false
    });
};

const showErrorAlert = (message) => {
    Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: message,
        padding: '2em',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
    });
};

// Initialize the form
onMounted(async () => {
    const violationId = route.params.id;
    if (violationId) {
        isEditMode.value = true;
        if (hasEditPermission.value) {
            await fetchViolation(violationId);
        }
    }
});

// Fetch single violation
const fetchViolation = async (id) => {
    try {
        loading.value = true;
        const response = await axios.get(`${BASE_URL}/violations/${id}`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        formData.value = response.data;
    } catch (error) {
        console.error('Error fetching violation:', error);
        handleApiError(error, 'Failed to load violation data');
    } finally {
        loading.value = false;
    }
};

// Handle form submission
const handleSubmit = async () => {
    if (!hasEditPermission.value) {
        showErrorAlert("You don't have permission to perform this action");
        return;
    }

    try {
        loading.value = true;

        if (isEditMode.value) {
            await axios.put(
                `${BASE_URL}/violations/${formData.value.id}`,
                formData.value,
                {
                    headers: {
                        'Authorization': `Bearer ${store.state.token}`,
                        'X-Company-ID': store.state.currentCompany?.id
                    }
                }
            );
            showSuccessAlert('Violation updated successfully!');
        } else {
            await axios.post(
                `${BASE_URL}/violations`,
                formData.value,
                {
                    headers: {
                        'Authorization': `Bearer ${store.state.token}`,
                        'X-Company-ID': store.state.currentCompany?.id
                    }
                }
            );
            showSuccessAlert('Violation created successfully!');
        }

        router.push('/violation/view');
    } catch (error) {
        console.error('Error saving violation:', error);
        handleApiError(error, 'Failed to save violation');
    } finally {
        loading.value = false;
    }
};

// Handle API errors
const handleApiError = (error, defaultMessage) => {
    let errorMessage = defaultMessage;

    if (error.response) {
        errorMessage = error.response.data.message || errorMessage;

        // Handle specific error cases
        if (error.response.status === 403) {
            errorMessage = "You don't have permission to perform this action";
        } else if (error.response.status === 404) {
            errorMessage = "Violation not found";
        }
    } else if (error.request) {
        errorMessage = 'Network error - please check your connection';
    }

    showErrorAlert(errorMessage);
};
</script>